import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { useDashboardAccounts } from "@/contexts/DashboardAccountContext";
import { PlatformBadge, PlatformIcon } from "./PlatformBadge";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Building2, ChevronDown, Check, Plus, Filter } from "lucide-react";
import { cn } from "@/lib/utils";

export function UniversalAccountSelector() {
  const {
    selectedAccountIds,
    toggleAccount,
    selectAll,
    selectPlatform,
    allAccounts,
    selectedAccounts,
    isAllSelected,
    activePlatforms,
    brandId: brandFilter,
    setBrandId: setBrandFilter,
  } = useDashboardAccounts();
  const [, navigate] = useLocation();
  const [open, setOpen] = useState(false);

  const { data: brands } = useQuery<Array<{ id: number; name: string }>>({
    queryKey: ["/api/brands"],
    queryFn: async () => {
      const res = await fetch("/api/brands");
      if (!res.ok) return [];
      return res.json();
    },
  });

  const { data: brandAccountLinks } = useQuery<Array<{ brandId: number; adAccountId: number }>>({
    queryKey: ["/api/ad-accounts/brand-links"],
    queryFn: async () => {
      const res = await fetch("/api/ad-accounts/brand-links");
      if (!res.ok) return [];
      return res.json();
    },
  });

  const filteredAccounts = useMemo(() => {
    if (!brandFilter || !brandAccountLinks) return allAccounts;
    const linkedIds = new Set(
      brandAccountLinks
        .filter((l) => l.brandId === brandFilter)
        .map((l) => l.adAccountId)
    );
    return allAccounts.filter(a => linkedIds.has(a.id));
  }, [allAccounts, brandFilter, brandAccountLinks]);

  const platformGroups = useMemo(() => {
    const map = new Map<string, { slug: string; name: string; color: string; accounts: typeof allAccounts }>();
    filteredAccounts.forEach(a => {
      if (a.platform) {
        const existing = map.get(a.platform.slug);
        if (existing) {
          existing.accounts.push(a);
        } else {
          map.set(a.platform.slug, {
            slug: a.platform.slug,
            name: a.platform.name,
            color: a.platform.color,
            accounts: [a],
          });
        }
      }
    });
    return Array.from(map.values());
  }, [filteredAccounts]);

  const selectedCount = selectedAccountIds.length;

  const displayName = isAllSelected
    ? `All Platforms (${allAccounts.length})`
    : selectedCount === 1
      ? allAccounts.find(a => a.id === selectedAccountIds[0])?.accountName || "1 Account"
      : activePlatforms.length === 1
        ? `All ${platformGroups.find(g => g.slug === activePlatforms[0])?.name || ""} (${selectedCount})`
        : `${selectedCount} Accounts`;

  const selectedPills = !isAllSelected && selectedCount > 0 && selectedCount <= 3
    ? selectedAccounts.slice(0, 3)
    : [];

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="text-sm gap-2 min-w-[160px] max-w-[280px] justify-between" data-testid="dashboard-account-selector">
          <div className="flex items-center gap-1.5 min-w-0">
            <Building2 className="h-4 w-4 shrink-0 text-gray-500" />
            {selectedPills.length > 0 ? (
              <div className="flex items-center gap-1 min-w-0">
                {selectedPills.map(acc => (
                  <span key={acc.id} className="inline-flex items-center gap-1 bg-gray-100 rounded px-1.5 py-0.5 text-[11px] max-w-[80px]">
                    {acc.platform && <PlatformIcon platform={acc.platform.slug} size="sm" />}
                    <span className="truncate">{acc.accountName.split(' - ').pop()}</span>
                  </span>
                ))}
              </div>
            ) : (
              <span className="truncate">{displayName}</span>
            )}
          </div>
          <ChevronDown className="h-3 w-3 shrink-0 text-gray-400" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-1 max-w-[min(320px,calc(100vw-24px))] w-80" align="end">
        <div className="flex flex-col max-h-[400px] overflow-auto">
          {brands && brands.length > 0 && (
            <>
              <p className="px-3 py-1.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wider flex items-center gap-1">
                <Filter className="h-3 w-3" />
                Filter by Brand
              </p>
              <div className="px-2 pb-1">
                <select
                  value={brandFilter ?? ""}
                  onChange={(e) => setBrandFilter(e.target.value ? Number(e.target.value) : null)}
                  className="w-full text-xs rounded-md px-2 py-1.5 focus:outline-none"
                  style={{ background: "#F7F9FC", border: "1px solid #DCE3ED", color: "#1A1A1A" }}
                  data-testid="dashboard-brand-filter"
                >
                  <option value="">All Brands</option>
                  {brands.map((b) => (
                    <option key={b.id} value={b.id}>{b.name}</option>
                  ))}
                </select>
              </div>
              <div className="border-t my-1" />
            </>
          )}
          <p className="px-3 py-1.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Quick Select</p>
          <button
            onClick={() => { selectAll(); }}
            className={cn(
              "flex items-center gap-2 px-3 py-2 text-sm rounded-md transition-colors text-left",
              isAllSelected ? "bg-primary/5 text-primary font-medium" : "hover:bg-gray-50 text-gray-700"
            )}
            data-testid="dashboard-account-option-all"
          >
            {isAllSelected && <Check className="h-3.5 w-3.5 text-primary shrink-0" />}
            {!isAllSelected && <span className="w-3.5 shrink-0" />}
            All Platforms
          </button>

          {platformGroups.map(g => {
            const platformAccountIds = g.accounts.map(a => a.id);
            const isPlatformSelected = !isAllSelected &&
              platformAccountIds.every(id => selectedAccountIds.includes(id)) &&
              platformAccountIds.length === selectedAccountIds.length;
            return (
              <button
                key={g.slug}
                onClick={() => selectPlatform(g.slug)}
                className={cn(
                  "flex items-center gap-2 px-3 py-2 text-sm rounded-md transition-colors text-left",
                  isPlatformSelected ? "bg-primary/5 text-primary font-medium" : "hover:bg-gray-50 text-gray-700"
                )}
                data-testid={`dashboard-quick-select-${g.slug}`}
              >
                {isPlatformSelected && <Check className="h-3.5 w-3.5 text-primary shrink-0" />}
                {!isPlatformSelected && <span className="w-3.5 shrink-0" />}
                <PlatformIcon platform={g.slug} size="sm" />
                <span>All {g.name} ({g.accounts.length})</span>
              </button>
            );
          })}

          {brandFilter && filteredAccounts.length === 0 && (
            <p className="px-3 py-2 text-xs text-gray-400 italic" data-testid="brand-filter-empty-note">
              No accounts linked to this brand
            </p>
          )}

          {platformGroups.map(group => (
            <div key={group.slug}>
              <div className="border-t my-1" />
              <p className="px-3 py-1.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
                <PlatformIcon platform={group.slug} size="sm" />
                {group.name} Accounts
              </p>
              {group.accounts.map(account => {
                const isSelected = selectedAccountIds.includes(account.id);
                return (
                  <button
                    key={account.id}
                    onClick={() => toggleAccount(account.id)}
                    className={cn(
                      "flex items-center gap-2 px-3 py-2 text-sm rounded-md transition-colors text-left w-full",
                      isSelected ? "bg-primary/5 text-primary" : "hover:bg-gray-50 text-gray-700"
                    )}
                    data-testid={`dashboard-account-option-${account.id}`}
                  >
                    <div className={cn(
                      "w-3.5 h-3.5 rounded border flex items-center justify-center flex-shrink-0",
                      isSelected ? "bg-primary border-primary" : "border-gray-300"
                    )}>
                      {isSelected && <Check className="h-2.5 w-2.5 text-white" />}
                    </div>
                    <span className="truncate">{account.accountName}</span>
                  </button>
                );
              })}
            </div>
          ))}

          <div className="border-t my-1" />
          <button
            onClick={() => { navigate("/canvas?settings=connections"); setOpen(false); }}
            className="flex items-center gap-2 px-3 py-2 text-sm rounded-md text-primary hover:bg-primary/5 transition-colors text-left"
            data-testid="dashboard-account-connect-new"
          >
            <Plus className="h-3.5 w-3.5 shrink-0" />
            Connect New Account
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
